import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatEuro, formatDate, formatApiError } from "@/lib/api";
import {
  Plus,
  Upload,
  FileDown,
  Send,
  Trash2,
  CheckCircle2,
  Circle,
  ArrowRight,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import DevisSendDialog from "./DevisSendDialog";
import EmailTrackingBadge from "@/components/EmailTrackingBadge";

export default function AdminDevis() {
  const [items, setItems] = useState([]);
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialog, setSendDialog] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [d, f, c] = await Promise.all([
        api.get("/admin/devis"),
        api.get("/admin/factures"),
        api.get("/admin/clients"),
      ]);
      setItems(d.data);
      setFactures(f.data);
      setClients(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.nom || "—";

  const facturesOf = (devisId) => factures.filter((f) => f.devis_id === devisId);
  const acompteOf = (devisId) => facturesOf(devisId).find((f) => f.type === "acompte");
  const soldeOf = (devisId) => facturesOf(devisId).find((f) => f.type === "solde");

  const downloadPdf = async (id, numero) => {
    try {
      const res = await api.get(`/admin/devis/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openSendDialog = (devis) => {
    const client = clients.find((c) => c.id === devis.client_id) || {};
    setSendDialog({ devis, client });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try {
      await api.delete(`/admin/devis/${id}`);
      setItems((s) => s.filter((d) => d.id !== id));
      toast.success("Devis supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createFacture = async (devis, type, pourcentage = null) => {
    const label =
      type === "acompte"
        ? `Facture d'acompte (${pourcentage ?? 30} %)`
        : "Facture de solde";
    if (!window.confirm(`Créer la ${label} pour ${devis.titre} ?`)) return;
    try {
      if (devis.status !== "accepte") {
        await api.patch(`/admin/devis/${devis.id}`, { status: "accepte" });
      }
      const payload = { devis_id: devis.id, type };
      if (pourcentage != null) payload.pourcentage = pourcentage;
      const { data } = await api.post("/admin/factures", payload);
      toast.success(`${label} créée — ${data.numero}`);
      await load();
      navigate(`/admin/factures/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const promptCustomAcompte = (devis) => {
    const raw = window.prompt(
      "Pourcentage d'acompte personnalisé (entre 1 et 99) :",
      "40",
    );
    if (raw == null) return;
    const pct = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(pct) || pct <= 0 || pct >= 100) {
      toast.error("Pourcentage invalide (1 à 99 attendu)");
      return;
    }
    createFacture(devis, "acompte", pct);
  };

  const promptCustomSolde = (devis, defaultPct = 100) => {
    const raw = window.prompt(
      "Pourcentage du solde à facturer (1 à 100). Ex. 100 pour la totalité, 70 si un acompte de 30 % a déjà été perçu hors logiciel :",
      String(defaultPct),
    );
    if (raw == null) return;
    const pct = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
      toast.error("Pourcentage invalide (1 à 100 attendu)");
      return;
    }
    createFacture(devis, "solde", pct);
  };

  return (
    <div data-testid="admin-devis-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-eyebrow mb-3">Devis</p>
          <h1 className="h-display text-4xl md:text-5xl">Carnet de devis.</h1>
          <p className="text-[13px] text-muted-foreground mt-3 max-w-2xl">
            Chaque devis suit trois étapes : devis envoyé → acompte → solde. Si l'acompte a déjà été perçu hors logiciel, vous pouvez émettre directement le solde (100 % ou personnalisé).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/devis/import"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase font-medium border border-border hover:border-primary hover:text-primary transition-colors"
            data-testid="devis-import"
          >
            <Upload size={13} /> Importer un PDF
          </Link>
          <Link to="/admin/devis/nouveau" className="btn-terra" data-testid="devis-new">
            <Plus size={14} /> Nouveau devis
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="text-serial">Chargement</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-[14px]">Aucun devis pour le moment.</p>
      ) : (
        <ul className="space-y-3" data-testid="devis-list">
          {items.map((d) => {
            const acompte = acompteOf(d.id);
            const solde = soldeOf(d.id);
            return (
              <li
                key={d.id}
                className="border border-border bg-background hover:border-foreground/30 transition-colors p-5 md:p-6"
                data-testid={`devis-row-${d.id}`}
              >
                {/* Header — devis identity + montant + actions PDF/Send/Delete */}
                <div className="flex flex-wrap items-start justify-between gap-4 pb-5 border-b border-border">
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-serial">{d.numero} · {formatDate(d.created_at)}</p>
                    <Link
                      to={`/admin/devis/${d.id}`}
                      className="font-serif text-xl md:text-2xl leading-tight hover:text-primary transition-colors mt-1 inline-block"
                    >
                      {d.titre || "(sans titre)"}
                    </Link>
                    <p className="text-[13px] text-muted-foreground mt-1">
                      {clientName(d.client_id)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <p className="font-serif text-2xl leading-none">{formatEuro(d.total_ttc)}<span className="text-[11px] text-muted-foreground ml-2">TTC</span></p>
                    <div className="flex items-center gap-4 text-muted-foreground">
                      <Link to={`/admin/devis/${d.id}`} title="Modifier le devis" className="hover:text-foreground" data-testid={`devis-edit-${d.id}`}>
                        <Pencil size={16} />
                      </Link>
                      <button title="Télécharger le PDF" onClick={() => downloadPdf(d.id, d.numero)} className="hover:text-foreground" data-testid={`devis-pdf-${d.id}`}>
                        <FileDown size={16} />
                      </button>
                      <button title="Envoyer par email" onClick={() => openSendDialog(d)} className="hover:text-primary" data-testid={`devis-send-${d.id}`}>
                        <Send size={16} />
                      </button>
                      <button title="Supprimer" onClick={() => remove(d.id)} className="hover:text-destructive" data-testid={`devis-delete-${d.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Workflow timeline — 3 stages */}
                <div className="pt-5 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-0 md:relative">
                  {/* Stage 1 — Devis */}
                  <Stage
                    n={1}
                    label="Devis"
                    state={d.status === "envoye" || d.status === "accepte" ? "done" : "pending"}
                    chip={
                      d.status === "envoye"
                        ? "Envoyé"
                        : d.status === "accepte"
                          ? "Accepté"
                          : d.status === "refuse"
                            ? "Refusé"
                            : "Brouillon"
                    }
                    chipTestId={`stage-devis-${d.id}`}
                    extra={<EmailTrackingBadge devis={d} testid={`email-tracking-${d.id}`} />}
                    action={
                      d.status === "brouillon" || d.status === "envoye" ? (
                        <button
                          onClick={() => openSendDialog(d)}
                          className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                          data-testid={`devis-action-send-${d.id}`}
                        >
                          {d.status === "envoye" ? "Renvoyer" : "Envoyer au client"}
                        </button>
                      ) : null
                    }
                  />

                  {/* Stage 2 — Acompte */}
                  <Stage
                    n={2}
                    label="Acompte 30 %"
                    state={acompte ? "done" : d.status === "accepte" || d.status === "envoye" ? "ready" : "pending"}
                    chip={
                      acompte
                        ? `${acompte.numero} · ${formatEuro(acompte.montant_ttc)}`
                        : "À émettre"
                    }
                    chipTestId={`stage-acompte-${d.id}`}
                    action={
                      acompte ? (
                        <Link
                          to={`/admin/factures/${acompte.id}`}
                          className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-foreground"
                          data-testid={`devis-action-acompte-view-${d.id}`}
                        >
                          Ouvrir la facture
                        </Link>
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`devis-action-acompte-${d.id}`}>
                          <span className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">Émettre&nbsp;:</span>
                          <button
                            onClick={() => createFacture(d, "acompte", 30)}
                            className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                            data-testid={`devis-action-acompte-30-${d.id}`}
                          >
                            30&nbsp;%
                          </button>
                          <span className="text-muted-foreground text-[11px]">·</span>
                          <button
                            onClick={() => createFacture(d, "acompte", 50)}
                            className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                            data-testid={`devis-action-acompte-50-${d.id}`}
                          >
                            50&nbsp;%
                          </button>
                          <span className="text-muted-foreground text-[11px]">·</span>
                          <button
                            onClick={() => promptCustomAcompte(d)}
                            className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                            data-testid={`devis-action-acompte-custom-${d.id}`}
                          >
                            Perso
                          </button>
                        </div>
                      )
                    }
                  />

                  {/* Stage 3 — Solde */}
                  <Stage
                    n={3}
                    label={solde ? `Solde ${solde.pourcentage}%` : "Solde"}
                    state={solde ? "done" : "ready"}
                    chip={
                      solde
                        ? `${solde.numero} · ${formatEuro(solde.montant_ttc)}`
                        : acompte
                          ? "À émettre"
                          : "Sans acompte"
                    }
                    chipTestId={`stage-solde-${d.id}`}
                    isLast
                    action={
                      solde ? (
                        <Link
                          to={`/admin/factures/${solde.id}`}
                          className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-foreground"
                          data-testid={`devis-action-solde-view-${d.id}`}
                        >
                          Ouvrir la facture
                        </Link>
                      ) : acompte ? (
                        <button
                          onClick={() => createFacture(d, "solde")}
                          className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                          data-testid={`devis-action-solde-${d.id}`}
                        >
                          Émettre le solde
                        </button>
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`devis-action-solde-direct-${d.id}`}>
                          <span className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">Émettre&nbsp;:</span>
                          <button
                            onClick={() => createFacture(d, "solde", 100)}
                            className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                            data-testid={`devis-action-solde-100-${d.id}`}
                          >
                            100&nbsp;%
                          </button>
                          <span className="text-muted-foreground text-[11px]">·</span>
                          <button
                            onClick={() => promptCustomSolde(d)}
                            className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-primary"
                            data-testid={`devis-action-solde-custom-${d.id}`}
                          >
                            Perso
                          </button>
                        </div>
                      )
                    }
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {sendDialog && (
        <DevisSendDialog
          devis={sendDialog.devis}
          client={sendDialog.client}
          onClose={() => setSendDialog(null)}
          onSent={load}
        />
      )}
    </div>
  );
}

function Stage({ n, label, state, chip, chipTestId, action, extra, isLast }) {
  // state : "done" | "ready" | "pending"
  const ringColor =
    state === "done"
      ? "bg-primary text-background border-primary"
      : state === "ready"
        ? "bg-background text-primary border-primary"
        : "bg-background text-muted-foreground border-border";
  const labelColor = state === "pending" ? "text-muted-foreground" : "text-foreground";
  const Icon = state === "done" ? CheckCircle2 : Circle;
  return (
    <div className="relative flex items-start gap-4 md:px-5 md:py-2">
      {/* Connector arrow on desktop */}
      {!isLast && (
        <span className="hidden md:flex absolute right-[-9px] top-3 text-border">
          <ArrowRight size={16} />
        </span>
      )}
      <div
        className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${ringColor}`}
        aria-hidden
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] tracking-[0.2em] uppercase ${labelColor}`}>
          {n} · {label}
        </p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <p
            className={`text-[14px] font-medium truncate ${state === "pending" ? "text-muted-foreground" : "text-foreground"}`}
            data-testid={chipTestId}
          >
            {chip}
          </p>
          {extra}
        </div>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}
